import { LoadingButton } from '@mui/lab'
import i18n from '@services/i18n/i18n'
import { ConfirmDialog } from '@shared/components/ui/ConfirmDialog'
import { CreateEditLayout } from '@shared/layouts/CreateEditLayout'
import { CreateCategoryInput, useCreateCategoryMutation } from '@typings/graphql'
import React from 'react'
import { FormProvider, useForm, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router'
import { useFileUpload } from '@hooks/useFileUpload'
import { DefaultSnackbar } from '@shared/components/ui/DefaultSnackbar'
import { useErrorMessage } from '@hooks/useErrorMessage'
import { EditableFile } from '@typings/files'
import { useUnsavedChanges } from '@hooks/useUnsavedChanges'

import { CategoryCreateEditInputs } from '../components/CategoryCreateEditInputs'
import { CategoriesProvider } from '../provider/CategoriesProvider'

type CategoryInputType = Omit<CreateCategoryInput, 'texts'> & {
  texts: Record<string, string>
  files: Record<string, EditableFile<any> | null>
}

const CategoriesCreateViewContent: React.FC = () => {
  const { t } = useTranslation()
  const { professionId, categoryId } = useParams<{ professionId: string, categoryId: string }>()

  const [loading, setLoading] = React.useState(false)
  const [showCreatedDialog, setShowCreatedDialog] = React.useState(false)
  const [showToast, setShowToast] = React.useState(false)
  const [created, setCreated] = React.useState(false)

  const { setMessageByCode, errorMessage } = useErrorMessage()

  const navigate = useNavigate()

  const { formState: { dirtyFields, isDirty, isValid, isSubmitted }, handleSubmit } = useFormContext()

  useUnsavedChanges(isDirty && !created && (!isSubmitted || !!errorMessage))

  const [createCategory] = useCreateCategoryMutation()

  const { uploadFile } = useFileUpload()

  const onSubmit = handleSubmit(async (input) => {
    setLoading(true)

    try {
      const result = await createCategory({
        variables: {
          data: {
            professionId: professionId as string,
            parentId: categoryId || input.parentId,
            texts: Object.keys(input.texts).map((key) => ({
              key,
              lang: i18n.resolvedLanguage,
              value: input.texts[key]
            }))
          }
        }
      })
      Object.keys(input.files)?.forEach(async (key) => {
        const file = input.files[key]

        if (file?.upload && result.data?.createCategory) {
          await uploadFile(file.upload.file, file.upload.data.key, 'Category', result.data.createCategory.id)
        }
      })

      setCreated(true)
      setShowCreatedDialog(true)
    } catch (e) {
      setMessageByCode(e)
      setShowToast(true)
    } finally {
      setLoading(false)
    }
  })

  const handleConfirm = () => {
    navigate(`/professions/${professionId}${categoryId ? `/${categoryId}` : ''}`)
  }

  return (
    <CreateEditLayout
      title={t(`${categoryId ? 'categories.subCreateTitle' : 'categories.createTitle'}`)}
      description={t(`${categoryId ? 'categories.subCreateDescription' : 'categories.createDescription'}`)}
      buttonComp={(
        <LoadingButton
          variant="contained"
          type='submit'
          loading={loading}
          onClick={onSubmit}
          disabled={!dirtyFields || !isValid}
        >
          {t('common.save')}
        </LoadingButton>
      )}
    >
      <CategoryCreateEditInputs loading={loading} />

      <ConfirmDialog
        title={t('common.entryCreated')}
        open={showCreatedDialog}
        hideCancel
        onConfirm={handleConfirm}
      >
        {t('categories.createCompleted')}
      </ConfirmDialog>

      <DefaultSnackbar
        open={showToast}
        severity={'error'}
        onClose={() => setShowToast(false)}
        message={errorMessage}
      />
    </CreateEditLayout>
  )
}

export const CategoriesCreateView: React.FC = () => {
  const formData = useForm<CategoryInputType>({
    defaultValues: {},
    mode: 'onChange'
  })

  return (
    <CategoriesProvider>
      <FormProvider {...formData}>
        <CategoriesCreateViewContent />
      </FormProvider>
    </CategoriesProvider>
  )
}
